import { FC, Fragment, useState } from "react";

import XLSX from "sheetjs-style";
import classNames from "classnames";
import * as FileSaver from "file-saver";
import { Menu, Transition } from "@headlessui/react";
import {
  formatSingleEventData,
  getExcelData,
} from "../../services/exportService";
import { useTranslation } from "react-i18next";
import moment from "moment";

type ExportExcelProps = {
  event: string;
  fileName: string;
  period: string[];
  perimeter?: string;
  institutionId?: string;
  status?: string;
  eventId?: number;
};

const ExportExcel: FC<ExportExcelProps> = ({
  event,
  fileName,
  institutionId,
  perimeter,
  status,
  period,
}) => {
  const { t } = useTranslation();
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";
  const [loading, setLoading] = useState(false);
  const convertDate = (date: string) => {
    if (date) {
      return moment(new Date(date)).format("YYYY-MM-DD");
    }
    return "Invalid Date";
  };
  const exportToExcel = async () => {
    setLoading(true);
    const { data } = await getExcelData({
      event,
      institutionId,
      perimeter,
      status,
      startDate: convertDate(period[0] ?? ""),
      endDate: convertDate(period[1] ?? ""),
    });
    const formatedData = event ? formatSingleEventData(data, t, event) : data;
    if (Array.isArray(formatedData) && formatedData.length > 0) {
      let wb = {
        Sheets: {},
        SheetNames: [""],
      };
      if (event) {
        const ws = XLSX.utils.json_to_sheet(formatedData);
        wb = {
          Sheets: { data: ws },
          SheetNames: ["data"],
        };
      } else {
        const sheetNames: string[] = [];

        const sheets = formatedData.reduce((acc, data) => {
          const sheetName = data._id;
          sheetNames.push(sheetName);
          const reports = formatSingleEventData(data.reportings, t, sheetName);
          const ws = XLSX.utils.json_to_sheet(reports);
          acc[sheetName] = ws;
          return acc;
        }, {});
        wb = {
          Sheets: sheets,
          SheetNames: sheetNames,
        };
      }
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="font-bold text-[#1E408E] border border-[#1E408E] rounded-full p-3 bg-white">
          Export
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-1 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            <Menu.Item>
              <button
                onClick={exportToExcel}
                className={classNames("block px-4 py-2 text-sm w-full")}
              >
                {loading ? "Exporting to Excel " : "Export to Excel"}
              </button>
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default ExportExcel;
